// Détection de la langue : préfixe d'adresse > choix mémorisé du visiteur > (suggestion selon la
// langue du navigateur) > français.  Tout accès au stockage est protégé (navigation privée, blocage).
import { DEFAULT_LANG, LANG_CODES, isEnabledLang } from './config';
import { langFromPath, stripLang, isRecipientPath, isAdminPath, localizedPath } from './paths';

const STORAGE_KEY = 'jm_lang';
const SUGGEST_KEY = 'jm_lang_suggestion_refusee';

// Le navigateur envoie la langue de l'appareil : on la rapproche d'une langue du site.
// Suisse alémanique (gsw), allemand de Suisse… → allemand ; « pt-BR » → portugais ; etc.
const ALIASES = { gsw: 'de', als: 'de', nds: 'de', nb: null, nn: null };

export const getBrowserLang = () => {
  try {
    const wanted = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
    for (const raw of wanted) {
      const base = String(raw || '').toLowerCase().split('-')[0];
      const code = base in ALIASES ? ALIASES[base] : base;
      if (code && isEnabledLang(code)) return code;
    }
  } catch (e) { /* navigateur sans langues déclarées */ }
  return null;
};

export const getStoredLang = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && isEnabledLang(v) ? v : null;
  } catch (e) { return null; }
};

export const storeLang = (code) => {
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* navigation privée */ }
};

export const isSuggestionDismissed = () => {
  try { return window.localStorage.getItem(SUGGEST_KEY) === '1'; } catch (e) { return false; }
};
export const dismissSuggestion = () => {
  try { window.localStorage.setItem(SUGGEST_KEY, '1'); } catch (e) { /* idem */ }
};

// ?lang=de dans un lien : la langue voulue par celui qui l'a envoyé (pages de destinataires).
export const getQueryLang = (search) => {
  try {
    const v = new URLSearchParams(search || '').get('lang');
    return v && isEnabledLang(v.toLowerCase()) ? v.toLowerCase() : null;
  } catch (e) { return null; }
};

/**
 * Langue à utiliser à l'arrivée sur une adresse.
 *  - préfixe d'adresse (/en/…)                          → cette langue ;
 *  - page de destinataire (carte, faire-part)           → ?lang=, sinon langue du navigateur, sinon français ;
 *  - administration                                     → français, jamais de redirection ;
 *  - adresse sans préfixe et choix mémorisé ≠ français  → on y redirige (redirectTo), c'est le choix du visiteur ;
 *  - sinon                                              → français (la langue du navigateur ne fait que SUGGÉRER,
 *                                                         voir LanguageSuggestion : pas de redirection automatique).
 * Renvoie { lang, prefix, redirectTo } ; prefix = langue lue dans l'adresse ('' pour le français).
 */
export const resolveInitialLang = ({ pathname, search = '', hash = '' }) => {
  // « /fr/… » n'existe pas (le français n'a pas de préfixe) : on renvoie vers l'adresse sans préfixe.
  const explicitDefault = new RegExp(`^/${DEFAULT_LANG}(?=/|$)`).exec(pathname);
  if (explicitDefault) {
    return { lang: DEFAULT_LANG, prefix: '', redirectTo: (pathname.slice(DEFAULT_LANG.length + 1) || '/') + search + hash };
  }
  const fromUrl = langFromPath(pathname);
  if (fromUrl) return { lang: fromUrl, prefix: fromUrl, redirectTo: null };

  if (isRecipientPath(pathname)) {
    return { lang: getQueryLang(search) || getBrowserLang() || DEFAULT_LANG, prefix: '', redirectTo: null };
  }

  // L'administration reste en français, sans redirection.
  if (isAdminPath(pathname)) return { lang: DEFAULT_LANG, prefix: '', redirectTo: null };

  const stored = LANG_CODES.length > 1 ? getStoredLang() : null;
  if (stored && stored !== DEFAULT_LANG) {
    return { lang: stored, prefix: stored, redirectTo: localizedPath(stripLang(pathname), stored) + search + hash };
  }
  return { lang: DEFAULT_LANG, prefix: '', redirectTo: null };
};

/** Langue à SUGGÉRER (bandeau discret) : navigateur dans une autre langue, aucun choix déjà fait. */
export const suggestedLang = (currentLang) => {
  if (currentLang !== DEFAULT_LANG || getStoredLang() || isSuggestionDismissed()) return null;
  const browser = getBrowserLang();
  return browser && browser !== currentLang ? browser : null;
};
